import { z } from 'zod';
import { AlertSeverity, AlertSignal } from './alert';
import { TxType } from './tx-type';

/**
 * 외부 연동 계약 (16회차, A안).
 *
 * OneWorks2에는 오류를 보는 화면이 없다. 소스를 못 고치니 **APM이 데이터를 내놓고**
 * OneWorks2 웹이 나중에 "시스템 상태" 페이지에서 그것을 부른다. 지금 정할 것은 그 JSON의
 * 모양이다. 여기가 곧 그쪽 프런트가 붙는 자리이므로 필드를 함부로 지우거나 뜻을 바꾸지 않는다.
 *
 * 내부 API(`/api/tx/*`·`/api/alerts`)와 따로 두는 이유가 셋이다.
 *   - 내부 API는 콘솔과 함께 회차마다 바뀐다. 밖에서 쓰는 것은 그 속도로 바뀌면 안 된다
 *   - 내부 API는 인증이 없다(바인드 주소 127.0.0.1이 유일한 방어선). 외부용은 토큰이 필요하다
 *   - 밖에 내보낼 값은 내부보다 좁다. 요약 행을 통째로 주지 않고 볼 값만 고른다
 *
 * `version`은 모든 응답에 실린다. **선택 칸을 더하는 것은 값을 올리지 않는다** — 받는 쪽이
 * 모르는 필드를 무시하면 그만이고, 판을 올리면 아직 안 쓰는 소비자까지 갈아타야 한다.
 * 값을 올리는 것은 **필수 칸을 더하거나, 뜻을 바꾸거나, 칸·값을 지울 때**다. 그때는 한동안
 * 둘 다 낸다 (리뷰 R26-13).
 *
 * 이 파일에는 **node 전용 import를 넣지 않는다.** 콘솔이 계약을 브라우저 번들로 가져간다.
 *
 * 필드마다 `.describe()`가 붙어 있다 (20회차). `GET /api/external/contract`가 이 설명을 그대로
 * 내보내므로, 설명이 없는 필드는 그쪽 계약 표에 빈칸으로 나온다. 테스트가 빈 설명을 막는다.
 */

const count = z.number().int().nonnegative();
const nonneg = z.number().nonnegative();
const epochMs = z.number().int().nonnegative();

/**
 * 지금 계약 판. 응답의 `version`과 같다.
 *
 * **판 2 (29회차).** `ExternalInstance.loopP99Ms`가 선택 칸이 됐다. 이벤트루프가 없는
 * 런타임(JVM 등)이 붙으면서 "언제나 숫자가 있다"는 판 1의 약속을 지킬 수 없게 됐기 때문이다.
 * 0으로 채우면 "밀리지 않는다"는 거짓말이 되므로 아예 비운다. 있던 보장을 지우는 변경이라
 * 위 규칙대로 판을 올린다 (리뷰 R29-08). 판 1을 보던 소비자는 그 칸이 없을 수 있다는 것만
 * 처리하면 나머지는 그대로다.
 *
 * **판 3 (34회차).** `ExternalServiceSummary.health`의 뜻이 바뀌었다. 스냅샷을 안 보내는
 * "트레이스만" 서비스가 조용해진 것을 더 이상 `critical`로 보지 않고, 대신 최근 5분 오류율을
 * 직접 본다. 칸이 늘거나 준 것이 아니라 **같은 칸의 값이 달라지는 변경**이라 위 규칙대로 판을
 * 올린다. 판 2를 보던 소비자에게는 밤사이 `critical`이던 앱이 `ok`로 바뀌어 보인다.
 * 30회차에 늘린 `includeGlobal` 파라미터처럼 **더하기만 한 것은 판을 올리지 않는다** —
 * 그때 판이 2에 머문 것과 같은 규칙이다.
 */
export const EXTERNAL_API_VERSION = 3;

const version = z
  .literal(EXTERNAL_API_VERSION)
  .describe('계약 판. 모양이 바뀌면 올라간다. 받는 쪽은 아는 판만 처리한다');

/**
 * 3단계 상태. 값은 `@apm/core`의 `statusLevel`이 정하는 것과 같다.
 * 계약이 core를 import하지 않는 이유는 의존 방향이 반대라서다(core가 계약을 본다).
 */
export const ExternalLevel = z.enum(['ok', 'fail', 'error']);
export type ExternalLevel = z.infer<typeof ExternalLevel>;

/** 화면에 그대로 쓸 수 있는 한글 라벨 */
export const EXTERNAL_LEVEL_LABEL: Record<ExternalLevel, string> = {
  ok: '정상',
  fail: '실패',
  error: '오류',
};

/**
 * 인스턴스 한 대의 지금 상태.
 *
 * 값은 두 곳에서 온다. 게이지(힙·이벤트루프·진행 중)는 1초 스냅샷이고, 오류 수는 끝난 요청
 * 피드의 최근 5분 창이다. sse는 연결이 수십 분 유지되므로 오류 집계에서 **뺀다**
 * (2026-09-07 결정 4). 열린 연결 수는 따로 낸다.
 */
export const ExternalInstance = z.object({
  service: z.string().min(1).describe('OTel service.name (예: oneworks2-api)'),
  instance: z.string().min(1).describe('OTel service.instance.id. 호스트명 + pid 등'),
  lastSeenAt: epochMs.describe(
    '마지막 스냅샷 시각. epoch ms. 이 값이 오래됐으면 그 프로세스가 조용한 것이다',
  ),
  activeTx: count.describe('지금 진행 중인 요청 수. sse는 빼고 센다'),
  activeSse: count.describe('지금 열려 있는 sse 연결 수'),
  errors5m: count.describe('최근 5분 오류 요청 수(5xx·예외). sse 제외'),
  fails5m: count.describe('최근 5분 실패 요청 수(4xx·요청 안의 SQL 오류). sse 제외'),
  heapUsedPct: nonneg.describe(
    '힙 사용률(%). 0~100. 분모는 V8 힙 상한이다(22-C회차). 옛 에이전트(상한 값 없음)는 확보한 힙 대비',
  ),
  rssBytes: count.describe('프로세스 RSS(바이트)'),
  loopP99Ms: nonneg
    .optional()
    .describe(
      '이벤트루프 p99 지연(ms). "처리가 밀리는지"를 보는 값이다. 이벤트루프가 없는 런타임(JVM 등)에는 없다 (29회차)',
    ),
  completedPerSec: count.describe('직전 1초에 끝난 요청 수'),
  errorsPerSec: count.describe('직전 1초에 오류로 끝난 요청 수'),
  level: ExternalLevel.describe(
    '인스턴스 한 줄 판정. 최근 5분에 오류가 있으면 error, 실패만 있으면 fail, 둘 다 없으면 ok',
  ),
  stale: z
    .boolean()
    .optional()
    .describe(
      '신호가 끊겼다 (26회차, 선택 칸). 참이면 위의 게이지는 마지막으로 받은 값이라 지금 상태가 아니다',
    ),
  restartedAt: epochMs
    .optional()
    .describe('이 이름으로 다른 프로세스가 뜬 것을 본 시각 (26회차). 재시작·재배포 때 찍힌다'),
  realtime: z
    .boolean()
    .optional()
    .describe(
      '1초 스냅샷을 보내는 인스턴스인가 (26회차, 선택 칸). 이 목록은 스냅샷에서 만들어 언제나 참이다. 트레이스만 있는 서비스는 /api/external/services 에서 본다',
    ),
});
export type ExternalInstance = z.infer<typeof ExternalInstance>;

/** `GET /api/external/status` 응답 */
export const ExternalStatus = z.object({
  version,
  at: epochMs.describe('응답을 만든 시각. epoch ms'),
  windowMs: count.describe('오류 수를 센 창의 길이(ms). 지금은 5분'),
  instances: z.array(ExternalInstance).describe('인스턴스별 지금 상태. 순서는 정해져 있지 않다'),
});
export type ExternalStatus = z.infer<typeof ExternalStatus>;

/**
 * 오류로 끝난 요청 한 건.
 *
 * 요약 행을 통째로 주지 않는다. SQL 시간·외부 호출 시간처럼 밖에서 볼 이유가 없는 값은 뺐다.
 * `errorMessage`는 예외 메시지 원문이다(2026-09-07 결정 3). 서명 토큰은 서버가 지운 뒤다.
 */
export const ExternalError = z.object({
  traceId: z
    .string()
    .regex(/^[0-9a-f]{32}$/)
    .describe('trace id. 32자리 hex. 관제 화면·로그의 trace_id와 같은 값'),
  service: z.string().min(1).describe('OTel service.name'),
  instance: z.string().optional().describe('어느 프로세스에서 났는지. 16회차 전 데이터에는 없다'),
  txType: TxType.describe('트랜잭션 타입. http·sse·mcp·worker·pdf·slow. 오류 목록에 sse는 없다'),
  entry: z.string().min(1).describe('진입점. http는 "METHOD /route"'),
  startedAt: epochMs.describe('요청 시작 시각. epoch ms'),
  durationMs: nonneg.describe('걸린 시간(ms)'),
  level: ExternalLevel.describe('error(5xx·예외) 또는 fail(4xx·요청 안의 SQL 오류)'),
  httpStatus: z
    .number()
    .int()
    .min(100)
    .max(599)
    .optional()
    .describe('HTTP 상태 코드. http가 아닌 타입에는 없다'),
  errorType: z.string().optional().describe('예외 클래스 이름. 예외 없이 끝난 4xx에는 없다'),
  errorMessage: z
    .string()
    .optional()
    .describe('예외 메시지 원문. 서명 토큰과 userinfo는 지운 뒤다. 예외 없이 끝난 4xx에는 없다'),
  sqlErrorCount: count.describe('요청 안에서 깨진 SQL 건수'),
  tenantId: z.string().optional().describe('테넌트 id. 공개 라우트·워커에는 없다'),
  detailPath: z
    .string()
    .min(1)
    .describe('APM 콘솔 상세 화면 경로. 앞에 관제 화면 주소를 붙이면 링크가 된다'),
});
export type ExternalError = z.infer<typeof ExternalError>;

/** `GET /api/external/errors` 응답 */
export const ExternalErrors = z.object({
  version,
  at: epochMs.describe('응답을 만든 시각. epoch ms. 다음 폴링의 since로 그대로 넣는다'),
  since: epochMs.describe('이 시각 이후에 관제 서버에 도착한 것만 담았다. epoch ms'),
  count: count.describe('errors에 담긴 건수'),
  truncated: z.boolean().describe('창 안에 더 있었지만 limit에 걸려 잘렸다'),
  errors: z.array(ExternalError).describe('오류로 끝난 요청. 최근 것이 앞이다'),
});
export type ExternalErrors = z.infer<typeof ExternalErrors>;

/**
 * 밖에 내놓는 알림 상태 (24회차 리뷰 7).
 *
 * 내부 `AlertState`에는 24회차에 `notice`(프로파일 수집·폭주 억제 통보)가 늘었지만
 * **버전 1의 외부 계약에는 넣지 않는다.** 필드를 더하는 것과 달리 enum 값을 더하면
 * `firing|resolved`만 받도록 짜 둔 소비자의 분기가 깨진다. 통보는 밖으로 내보내지 않고,
 * 필요해지면 외부 API 버전을 올려 함께 연다.
 */
export const ExternalAlertState = z.enum(['firing', 'resolved']);
export type ExternalAlertState = z.infer<typeof ExternalAlertState>;

/** 경보 중인 알림 한 건. 내부 `AlertEvent`에서 밖에서 볼 값만 골랐다 */
export const ExternalAlert = z.object({
  id: z.string().min(1).describe('알림 사건 id. 발화부터 해제까지 같은 값'),
  ruleId: z.string().min(1).describe('규칙 id. 규칙 파일의 rules[].id'),
  ruleName: z.string().min(1).describe('규칙 이름. 사람이 읽는 한글'),
  severity: AlertSeverity.describe('심각도'),
  signal: AlertSignal.optional().describe('어떤 신호를 보는 규칙인지'),
  key: z.string().min(1).describe('keyBy로 쪼갠 값. 안 쪼개면 all'),
  service: z
    .string()
    .optional()
    .describe(
      '이 알림이 가리키는 service.name (26회차). 규칙의 키가 서비스를 못 박을 때만 있다. DB·전체 규칙 알림에는 없다',
    ),
  instance: z
    .string()
    .optional()
    .describe('이 알림이 가리키는 인스턴스 (26회차). 규칙이 인스턴스로 쪼갤 때만 있다'),
  state: ExternalAlertState.describe('firing이면 경보 중, resolved면 해제됨'),
  value: z.number().describe('관측값'),
  threshold: z.number().describe('임계값'),
  startedAt: epochMs.describe('발화 시각. epoch ms'),
  endedAt: epochMs.optional().describe('해제 시각. epoch ms. 경보 중이면 없다'),
  message: z.string().min(1).describe('사람이 읽는 한글 한 줄. 그대로 화면에 쓸 수 있다'),
  notified: z
    .boolean()
    .describe(
      '쿨다운·분당 상한을 통과해 외부 채널로 보내기로 했는지. 채널이 받았다는 뜻은 아니다(전송 실패·주소 없음이어도 true). false면 발송만 억제된 것이다',
    ),
});
export type ExternalAlert = z.infer<typeof ExternalAlert>;

/** 서비스 한 줄 (26회차). 내부 `ServiceSummary`와 같은 값이고 설명만 붙어 있다 */
export const ExternalServiceSummary = z.object({
  service: z.string().min(1).describe('OTel service.name. nameFromFolder가 참이면 폴더 접두사다'),
  nameFromFolder: z
    .boolean()
    .default(false)
    .describe('이름을 저장 폴더에서 되짚었다. 참이면 service 조건으로 그대로 쓰면 안 맞을 수 있다'),
  instances: z
    .object({
      total: count.describe('아는 인스턴스 수'),
      stale: count.describe('그중 신호가 끊긴 수. 보고 방식을 가리지 않은 합'),
      realtime: count.optional().describe('그중 1초 스냅샷을 보내는 수 (34회차)'),
      traceOnly: count.optional().describe('그중 트레이스만 보내는 수 (34회차)'),
      staleRealtime: count
        .optional()
        .describe('조용해진 것 중 실시간 인스턴스 수. 이쪽만 신호가 끊긴 것이다 (34회차)'),
      staleTraceOnly: count
        .optional()
        .describe('조용해진 것 중 트레이스만 인스턴스 수. 요청이 없었다는 뜻이다 (34회차)'),
    })
    .describe('인스턴스 수와 그중 조용해진 수. 보고 방식으로 나눈 수는 34회차에 늘었다'),
  realtime: z
    .boolean()
    .describe('1초 스냅샷을 보내는 인스턴스가 있는가. 거짓이면 트레이스만 있는 서비스다'),
  lastSeenAt: epochMs
    .optional()
    .describe(
      '마지막 신호 시각. 아무 기록도 없으면 없다. 관제 서버가 다시 뜬 뒤에는 저장에 남은' +
        ' 마지막 기록으로 답하고 lastSeenFromStorage 가 선다 (34회차)',
    ),
  lastSeenFromStorage: z
    .boolean()
    .optional()
    .describe(
      'lastSeenAt 이 저장 파일에서 온 값인가. 참이면 이 서버가 받은 요청은 아직 없다 (34회차)',
    ),
  last5m: z
    .object({
      requests: count.describe('최근 5분 요청 수. sse 제외'),
      errorRate: nonneg.describe('오류 건수 / 요청 수. 0~1'),
      p95Ms: nonneg.describe('최근 5분 응답시간 p95(ms)'),
    })
    .describe('최근 5분 요약'),
  activeAlerts: count.describe('지금 발화 중인 알림 중 이 서비스 것'),
  storage: z
    .object({
      hotBytes: count.describe('핫(NDJSON) 용량. 바이트'),
      coldBytes: count.describe('콜드(Parquet) 용량. 바이트'),
    })
    .describe('이 서비스가 쓰고 있는 저장 용량'),
  health: z
    .enum(['ok', 'warning', 'critical'])
    .describe(
      '셋 중 가장 나쁜 것 (판 3). 활성 알림의 최고 심각도, 1초 스냅샷을 보내는 인스턴스의 생존' +
        '(전부 조용하면 critical), 최근 5분 오류율(20건 이상일 때 5% 이상 warning, 20% 이상' +
        ' critical). 트레이스만 서비스가 조용한 것은 요청이 없었다는 뜻이라 위험으로 보지 않는다',
    ),
});
export type ExternalServiceSummary = z.infer<typeof ExternalServiceSummary>;

/** `GET /api/external/alerts` 응답 */
export const ExternalAlerts = z.object({
  version,
  at: epochMs.describe('응답을 만든 시각. epoch ms'),
  count: count.describe('alerts에 담긴 건수'),
  alerts: z.array(ExternalAlert).describe('지금 경보 중인 알림. 시작이 이른 것이 앞이다'),
});
export type ExternalAlerts = z.infer<typeof ExternalAlerts>;

/**
 * 서비스 롤업 (26회차). 내부 `GET /api/services`와 **같은 줄**을 준다.
 *
 * 인스턴스 목록(`status`)은 1초 스냅샷에서 만들어 트레이스만 있는 서비스가 아예 안 나온다.
 * 이쪽은 그런 서비스도 포함해 "지금 붙어 있는 앱이 무엇무엇인가"를 한 번에 답한다.
 * 줄은 내부 계약(`ServiceSummary`)과 같고 봉투만 다르다 — 배열 칸 이름이 `rows`가 아니라
 * `services`이고 `version`이 얹힌다.
 */
export const ExternalServices = z.object({
  version,
  at: epochMs.describe('응답을 만든 시각. epoch ms'),
  windowMs: count.describe('last5m을 센 창의 길이(ms). 지금은 5분'),
  services: z.array(ExternalServiceSummary).describe('서비스별 한 줄. 이름순'),
});
export type ExternalServices = z.infer<typeof ExternalServices>;

/**
 * `GET /api/external/stream`이 미는 것 (SSE).
 *
 * SSE `event:` 이름이 곧 `type`이다. 셋을 한 스키마로 묶어 두면 받는 쪽이 어느 이벤트든
 * 같은 방법으로 검증할 수 있다.
 *   `status` 인스턴스 상태가 바뀔 때(1초 스냅샷마다). 목록 전체를 보낸다
 *   `error`  오류로 끝난 요청이 생길 때. 한 묶음씩
 *   `alert`  알림이 발화·복구될 때
 */
export const ExternalEvent = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('status').describe('SSE 이벤트 이름과 같다'),
    version,
    at: epochMs.describe('보낸 시각. epoch ms'),
    windowMs: count.describe('오류 수를 센 창의 길이(ms)'),
    instances: z.array(ExternalInstance).describe('인스턴스 목록 전체'),
  }),
  z.object({
    type: z.literal('error').describe('SSE 이벤트 이름과 같다'),
    version,
    at: epochMs.describe('보낸 시각. epoch ms'),
    errors: z.array(ExternalError).describe('이번에 확정된 오류 요청 묶음'),
  }),
  z.object({
    type: z.literal('alert').describe('SSE 이벤트 이름과 같다'),
    version,
    at: epochMs.describe('보낸 시각. epoch ms'),
    alert: ExternalAlert.describe('발화하거나 해제된 알림 한 건'),
  }),
]);
export type ExternalEvent = z.infer<typeof ExternalEvent>;

// ---- 요청 쪽 (20회차) ----

/** 응답 형식. 기본은 json이고 `?format=` 또는 `Accept`로 고른다 */
export const ExternalFormat = z.enum(['json', 'csv', 'ndjson']);
export type ExternalFormat = z.infer<typeof ExternalFormat>;

const format = ExternalFormat.optional().describe(
  '응답 형식. 없으면 json. Accept 헤더(text/csv·application/x-ndjson)보다 이 값이 우선한다. csv·ndjson은 봉투 없이 행만 준다',
);

/** `GET /api/external/status`의 쿼리 */
export const ExternalStatusQuery = z.object({
  service: z.string().optional().describe('이 service.name의 인스턴스만'),
  format,
});
export type ExternalStatusQuery = z.infer<typeof ExternalStatusQuery>;

/** `GET /api/external/errors`의 쿼리 */
export const ExternalErrorsQuery = z.object({
  since: epochMs
    .optional()
    .describe('이 시각(epoch ms) 뒤에 관제 서버에 도착한 것만. 직전 응답의 at을 그대로 넣는다'),
  limit: count
    .optional()
    .describe('최대 건수. 기본 100, 상한 1000 (APM_EXTERNAL_DEFAULT_LIMIT·APM_EXTERNAL_MAX_LIMIT)'),
  service: z.string().optional().describe('service.name 동등 비교'),
  instance: z.string().optional().describe('service.instance.id 동등 비교'),
  level: z.enum(['error', 'fail']).optional().describe('없으면 둘 다'),
  format,
});
export type ExternalErrorsQuery = z.infer<typeof ExternalErrorsQuery>;

/** `GET /api/external/services`의 쿼리 */
export const ExternalServicesQuery = z.object({ format });

/** `GET /api/external/alerts`의 쿼리 */
export const ExternalAlertsQuery = z.object({
  service: z
    .string()
    .optional()
    .describe(
      '이 service.name의 알림만 (26회차). 서비스가 없는 알림(DB·전체 규칙)은 조건을 주면 빠진다',
    ),
  includeGlobal: z
    .enum(['1', 'true', '0', 'false'])
    .optional()
    .describe('service와 함께 쓴다. 1·true면 서비스가 없는 알림(DB·전체 규칙)도 함께 준다'),
  format,
});
export type ExternalAlertsQuery = z.infer<typeof ExternalAlertsQuery>;

/** `GET /api/external/stream`의 쿼리 */
export const ExternalStreamQuery = z.object({
  token: z
    .string()
    .optional()
    .describe('Bearer 대신 쓰는 토큰. EventSource가 헤더를 못 붙여서 SSE만 받는다'),
  service: z
    .string()
    .optional()
    .describe(
      '이 service.name의 status·error·alert만 (26회차부터 alert에도 걸린다). 서비스가 없는 알림은 빠진다',
    ),
  includeGlobal: z
    .enum(['1', 'true', '0', 'false'])
    .optional()
    .describe(
      'service와 함께 쓴다. 1·true면 서비스가 없는 알림(DB·전체 규칙)도 alert 이벤트로 온다',
    ),
});
export type ExternalStreamQuery = z.infer<typeof ExternalStreamQuery>;

// ---- 계약 자체 (20회차) ----

/**
 * `GET /api/external/contract`가 내놓는 계약 한 장.
 *
 * 위 스키마에서 **기계가 만든다**(`buildExternalContract`). 손으로 적은 표가 아니라 스키마가 바뀌면
 * 같이 바뀐다. OneWorks2 쪽이 붙기 전에 필드 이름·타입·뜻·예시를 이것으로 읽고, 관제 화면의
 * "외부 연동" 메뉴도 이것을 표로 그린다.
 */
export const ExternalContractField = z.object({
  name: z.string().min(1).describe('필드 경로. 배열 안의 필드는 instances[].service처럼 적는다'),
  type: z.string().min(1).describe('JSON 타입. integer·number·string·boolean·array·object'),
  required: z.boolean().describe('항상 있는 필드인지. false면 없을 수 있다'),
  description: z.string().describe('뜻. 스키마의 describe()에서 온다'),
  enum: z.array(z.string()).optional().describe('값이 정해진 필드의 허용 값'),
  const: z
    .union([z.string(), z.number(), z.boolean()])
    .optional()
    .describe('값이 하나뿐인 필드(version 등)의 그 값'),
  pattern: z.string().optional().describe('문자열 모양(정규식)'),
  example: z.unknown().optional().describe('예시 응답에서 뽑은 값'),
});
export type ExternalContractField = z.infer<typeof ExternalContractField>;

export const ExternalContractParam = z.object({
  name: z.string().min(1).describe('쿼리 파라미터 이름'),
  type: z.string().min(1).describe('JSON 타입'),
  required: z.boolean().describe('꼭 넣어야 하는지'),
  description: z.string().describe('뜻'),
  enum: z.array(z.string()).optional().describe('허용 값'),
});
export type ExternalContractParam = z.infer<typeof ExternalContractParam>;

/** SSE 이벤트 한 종류. `type`이 곧 `event:` 이름이다 */
export const ExternalContractEvent = z.object({
  type: z.string().min(1).describe('SSE 이벤트 이름'),
  description: z.string().describe('언제 오는지'),
  fields: z.array(ExternalContractField),
  example: z.unknown().describe('이벤트 data 한 건의 예시'),
});
export type ExternalContractEvent = z.infer<typeof ExternalContractEvent>;

export const ExternalContractEndpoint = z.object({
  method: z.literal('GET'),
  path: z.string().min(1).describe('경로. 관제 서버 주소 뒤에 붙인다'),
  description: z.string().describe('무엇을 주는지'),
  kind: z.enum(['json', 'sse']).describe('json은 한 번 답하고 끝, sse는 이벤트를 계속 민다'),
  formats: z.array(ExternalFormat).describe('고를 수 있는 응답 형식. sse는 없다'),
  rows: z
    .string()
    .optional()
    .describe('csv·ndjson에서 행이 되는 배열 필드 이름. 봉투의 나머지 값은 응답 헤더로 간다'),
  params: z.array(ExternalContractParam).describe('쿼리 파라미터'),
  fields: z.array(ExternalContractField).describe('json 응답의 필드. 배열 안까지 평탄하게 편다'),
  events: z.array(ExternalContractEvent).optional().describe('sse일 때 이벤트 종류'),
  example: z.unknown().optional().describe('json 응답 예시 한 벌'),
});
export type ExternalContractEndpoint = z.infer<typeof ExternalContractEndpoint>;

/** `GET /api/external/contract` 응답 */
export const ExternalContract = z.object({
  version,
  at: epochMs.describe('응답을 만든 시각. epoch ms'),
  auth: z.string().describe('인증 방법 설명'),
  endpoints: z.array(ExternalContractEndpoint),
});
export type ExternalContract = z.infer<typeof ExternalContract>;

// ---- 예시 (20회차) ----

const EXAMPLE_TRACE_ID = '320dc0510358505f7fff3a9dfbcc410e';

const exampleInstance: ExternalInstance = {
  service: 'oneworks2-api',
  instance: 'iconnect:23260',
  lastSeenAt: 1788754204093,
  activeTx: 0,
  activeSse: 12,
  errors5m: 2,
  fails5m: 4,
  heapUsedPct: 94,
  rssBytes: 80379904,
  loopP99Ms: 11.7,
  completedPerSec: 0,
  errorsPerSec: 0,
  level: 'error',
  stale: false,
  realtime: true,
};

const exampleError: ExternalError = {
  traceId: EXAMPLE_TRACE_ID,
  service: 'oneworks2-api',
  instance: 'iconnect:23260',
  txType: 'http',
  entry: 'GET /api/v2/knowledge/articles',
  startedAt: 1788754164969,
  durationMs: 28.2391,
  level: 'error',
  httpStatus: 500,
  errorType: 'QueryFailedError',
  errorMessage: "PostgreSQL error of type 'error' occurred (code: 42P01)",
  sqlErrorCount: 1,
  tenantId: '1',
  detailPath: `#/tx/${EXAMPLE_TRACE_ID}`,
};

const exampleAlert: ExternalAlert = {
  id: 'tx-error-rate-oneworks2-api-1788754100000',
  ruleId: 'tx-error-rate',
  ruleName: '요청 오류율',
  severity: 'critical',
  signal: 'tx_error_rate',
  key: 'oneworks2-api',
  service: 'oneworks2-api',
  state: 'firing',
  value: 12.5,
  threshold: 5,
  startedAt: 1788754100000,
  message: '[critical] oneworks2-api 오류율 12.5% > 5%',
  notified: true,
};

const exampleService: ExternalServiceSummary = {
  service: 'oneworks2-api',
  nameFromFolder: false,
  instances: { total: 2, stale: 0, realtime: 2, traceOnly: 0, staleRealtime: 0, staleTraceOnly: 0 },
  realtime: true,
  lastSeenAt: 1788754204093,
  last5m: { requests: 812, errorRate: 0.004, p95Ms: 148.2 },
  activeAlerts: 1,
  storage: { hotBytes: 20971520, coldBytes: 314572800 },
  health: 'warning',
};

/**
 * 응답 예시. 계약 엔드포인트와 문서가 같은 것을 보여 준다.
 * 스키마를 통과하는지 테스트가 본다. 모양이 바뀌면 여기도 같이 바뀌어야 한다.
 */
export const EXTERNAL_EXAMPLES = {
  status: {
    version: EXTERNAL_API_VERSION,
    at: 1788754204578,
    windowMs: 300000,
    instances: [exampleInstance],
  } satisfies ExternalStatus,
  errors: {
    version: EXTERNAL_API_VERSION,
    at: 1788754204584,
    since: 1788753904584,
    count: 1,
    truncated: false,
    errors: [exampleError],
  } satisfies ExternalErrors,
  alerts: {
    version: EXTERNAL_API_VERSION,
    at: 1788754204600,
    count: 1,
    alerts: [exampleAlert],
  } satisfies ExternalAlerts,
  services: {
    version: EXTERNAL_API_VERSION,
    at: 1788754204611,
    windowMs: 300000,
    services: [exampleService],
  } satisfies ExternalServices,
  stream: {
    status: {
      type: 'status',
      version: EXTERNAL_API_VERSION,
      at: 1788754204578,
      windowMs: 300000,
      instances: [exampleInstance],
    } satisfies ExternalEvent,
    error: {
      type: 'error',
      version: EXTERNAL_API_VERSION,
      at: 1788754171000,
      errors: [exampleError],
    } satisfies ExternalEvent,
    alert: {
      type: 'alert',
      version: EXTERNAL_API_VERSION,
      at: 1788754100000,
      alert: exampleAlert,
    } satisfies ExternalEvent,
  },
} as const;
